import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { Save, Plus, RefreshCw, CircleCheck } from 'lucide-react'
import styles from './CSMSettings.module.css'
import { SM_DEFAULT_SETTINGS } from './smMockData'
import AuthContext from '../../store/auth-context'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import { smPost } from './smApi'
import { CUSTOM_OCCASION_ICON_OPTIONS, getIconComponent } from './smIcons'

const SM_GET_STORE_OCCASIONS = 'sm_get_store_occasions'
const SM_GET_OCCASION_CATALOG = 'sm_get_occasion_catalog'
const SM_ENABLE_STORE_OCCASIONS = 'sm_enable_store_occasions'
const SM_CREATE_CUSTOM_OCCASION = 'sm_create_custom_occasion'
const SENDER_OPTIONS = ['Salon WhatsApp', 'SMS Gateway', 'Email']

const Toggle = ({ checked, onChange }) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        className={`${styles.toggle} ${checked ? styles.toggleOn : ''}`}
        onClick={onChange}
    >
        <span className={styles.toggleKnob} />
    </button>
)

const CSMSettings = () => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    // Reminders / notification time / default sender have no backend
    // endpoint yet (no sm_get_settings / sm_save_settings) — still mock.
    const [settings, setSettings] = useState(SM_DEFAULT_SETTINGS)

    // Occasions Enabled — real data, both fixed (birthday, anniversary...)
    // and custom ones, fetched from sm_get_store_occasions. enabled_only:
    // false so disabled occasions still show up here for re-enabling.
    const [storeOccasions, setStoreOccasions] = useState([])
    const [loading, setLoading] = useState(true)
    const [loadErrorMessage, setLoadErrorMessage] = useState(null)

    const [newOccasionLabel, setNewOccasionLabel] = useState('')
    const [newOccasionIcon, setNewOccasionIcon] = useState(CUSTOM_OCCASION_ICON_OPTIONS[0].key)
    const [creatingOccasion, setCreatingOccasion] = useState(false)

    // The full system catalog (birthday, anniversary, business_anniversary,
    // child_birthday...) plus whether this store already has each one
    // enabled — this is how a brand-new store turns anything on for the
    // first time; sm_get_store_occasions alone can't show "not yet enabled"
    // entries since no store_occasions row exists for them yet.
    const [catalog, setCatalog] = useState([])
    const [catalogLoading, setCatalogLoading] = useState(true)
    const [catalogErrorMessage, setCatalogErrorMessage] = useState(null)
    const [selectedCatalogIds, setSelectedCatalogIds] = useState([])
    const [enabling, setEnabling] = useState(false)

    const loadStoreOccasions = useCallback(async () => {
        setLoading(true)
        setLoadErrorMessage(null)
        const res = await smPost(authCtx, activeStore, SM_GET_STORE_OCCASIONS, { enabled_only: false })
        if (res?.status === 'success' && res?.response) {
            setStoreOccasions(res.response.occasions || [])
        } else {
            setLoadErrorMessage(res?.message || "Couldn't load occasion settings right now.")
        }
        setLoading(false)
    }, [authCtx, activeStore])

    const loadCatalog = useCallback(async () => {
        setCatalogLoading(true)
        setCatalogErrorMessage(null)
        const res = await smPost(authCtx, activeStore, SM_GET_OCCASION_CATALOG)
        if (res?.status === 'success' && res?.response) {
            setCatalog(res.response.catalog || [])
        } else {
            setCatalogErrorMessage(res?.message || "Couldn't load the occasion catalog right now.")
        }
        setCatalogLoading(false)
    }, [authCtx, activeStore])

    useEffect(() => {
        if (activeStore?.storeIdentifier) {
            loadStoreOccasions()
            loadCatalog()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier])

    const toggleCatalogSelection = (occasionCatalogId) => {
        setSelectedCatalogIds((prev) =>
            prev.includes(occasionCatalogId)
                ? prev.filter((id) => id !== occasionCatalogId)
                : [...prev, occasionCatalogId]
        )
    }

    const handleEnableSelected = async () => {
        if (selectedCatalogIds.length === 0) {
            toast.error('Pick at least one occasion type to enable.')
            return
        }

        setEnabling(true)
        const res = await smPost(authCtx, activeStore, SM_ENABLE_STORE_OCCASIONS, {
            occasion_catalog_ids: selectedCatalogIds,
        })
        setEnabling(false)

        if (res?.status === 'success' && res?.response) {
            const count = res.response.enabled?.length ?? selectedCatalogIds.length
            toast.success(`Enabled ${count} occasion type${count === 1 ? '' : 's'}`)
            setSelectedCatalogIds([])
            loadCatalog()
            loadStoreOccasions()
        } else {
            toast.error(res?.message || "Couldn't enable these occasion types right now.")
        }
    }

    // NOTE: local-only — there's no endpoint yet to persist a toggle
    // (no sm_save_settings / sm_toggle_store_occasion), so this reverts
    // on refresh until that exists.
    const toggleStoreOccasion = (storeOccasionId) => {
        setStoreOccasions((prev) =>
            prev.map((o) => (o.store_occasion_id === storeOccasionId ? { ...o, is_enabled: !o.is_enabled } : o))
        )
    }

    const toggleReminder = (key) => {
        setSettings((prev) => ({
            ...prev,
            reminders: { ...prev.reminders, [key]: !prev.reminders[key] },
        }))
    }

    const handleSave = () => {
        toast.success('Special Moments settings saved')
    }

    const handleCreateCustomOccasion = async () => {
        const label = newOccasionLabel.trim()
        if (!label) {
            toast.error('Give the custom occasion a name first.')
            return
        }

        setCreatingOccasion(true)
        const res = await smPost(authCtx, activeStore, SM_CREATE_CUSTOM_OCCASION, {
            label,
            icon: newOccasionIcon,
        })
        setCreatingOccasion(false)

        if (res?.status === 'success' && res?.response) {
            setNewOccasionLabel('')
            setNewOccasionIcon(CUSTOM_OCCASION_ICON_OPTIONS[0].key)
            toast.success(`"${label}" added`)
            loadStoreOccasions()
        } else {
            toast.error(res?.message || "Couldn't create this occasion right now.")
        }
    }

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <h1 className={styles.pageTitle}>Special Moments Settings</h1>
                <p className={styles.pageSubtitle}>Choose which occasions to track and how reminders reach your team.</p>
            </div>

            <div className={styles.sectionLabel}>Occasions Enabled</div>
            <div className={styles.card}>
                {loading && (
                    <div className={styles.rowLoading}>
                        <RefreshCw size={14} className={styles.spinner} /> Loading occasions…
                    </div>
                )}

                {!loading && loadErrorMessage && (
                    <div className={styles.rowLoading}>
                        {loadErrorMessage}{' '}
                        <button type="button" className={styles.retryBtn} onClick={loadStoreOccasions}>Retry</button>
                    </div>
                )}

                {!loading && !loadErrorMessage && storeOccasions.length === 0 && (
                    <div className={styles.rowLoading}>Nothing enabled yet — pick some below to get started.</div>
                )}

                {!loading && !loadErrorMessage && storeOccasions.map((o) => {
                    const Icon = getIconComponent(o.icon)
                    return (
                        <div key={o.store_occasion_id} className={styles.row}>
                            <span className={styles.rowLabel}>
                                <span
                                    className={styles.occasionSwatch}
                                    style={{ background: `${o.color_code}1a`, color: o.color_code }}
                                >
                                    <Icon size={14} />
                                </span>
                                {' '}{o.label}
                            </span>
                            <Toggle checked={o.is_enabled} onChange={() => toggleStoreOccasion(o.store_occasion_id)} />
                        </div>
                    )
                })}
            </div>

            <div className={styles.sectionLabel}>Enable More Occasion Types</div>
            <div className={styles.card}>
                {catalogLoading && (
                    <div className={styles.rowLoading}>
                        <RefreshCw size={14} className={styles.spinner} /> Loading catalog…
                    </div>
                )}

                {!catalogLoading && catalogErrorMessage && (
                    <div className={styles.rowLoading}>
                        {catalogErrorMessage}{' '}
                        <button type="button" className={styles.retryBtn} onClick={loadCatalog}>Retry</button>
                    </div>
                )}

                {!catalogLoading && !catalogErrorMessage && catalog.map((c) => {
                    const Icon = getIconComponent(c.icon)
                    const isSelected = selectedCatalogIds.includes(c.occasion_catalog_id)
                    return (
                        <label
                            key={c.occasion_catalog_id}
                            className={`${styles.row} ${styles.catalogRow} ${c.already_enabled ? styles.catalogRowEnabled : ''}`}
                        >
                            <span className={styles.rowLabel}>
                                <span
                                    className={styles.occasionSwatch}
                                    style={{ background: `${c.color_code}1a`, color: c.color_code }}
                                >
                                    <Icon size={14} />
                                </span>
                                {' '}{c.label}
                            </span>
                            {c.already_enabled ? (
                                <span className={styles.enabledBadge}>
                                    <CircleCheck size={13} /> Enabled
                                </span>
                            ) : (
                                <input
                                    type="checkbox"
                                    className={styles.catalogCheckbox}
                                    checked={isSelected}
                                    onChange={() => toggleCatalogSelection(c.occasion_catalog_id)}
                                />
                            )}
                        </label>
                    )
                })}

                {!catalogLoading && !catalogErrorMessage && catalog.length > 0 && (
                    <div className={styles.enableSelectedRow}>
                        <button
                            type="button"
                            className={styles.addOccasionBtn}
                            onClick={handleEnableSelected}
                            disabled={enabling || selectedCatalogIds.length === 0}
                        >
                            {enabling ? <RefreshCw size={13} className={styles.spinner} /> : <Plus size={13} />}
                            {' '}Enable Selected{selectedCatalogIds.length > 0 ? ` (${selectedCatalogIds.length})` : ''}
                        </button>
                    </div>
                )}
            </div>

            <div className={styles.sectionLabel}>Add a Custom Occasion</div>
            <div className={styles.customOccasionForm}>
                <p className={styles.customOccasionHint}>
                    Not in the list above? Create your own — like a branch anniversary or a loyalty milestone.
                </p>

                <label className={styles.formFieldLabel}>Occasion Name</label>
                <input
                    type="text"
                    className={styles.customOccasionInput}
                    placeholder="e.g. Store Anniversary — Branch Opening"
                    value={newOccasionLabel}
                    onChange={(e) => setNewOccasionLabel(e.target.value)}
                />

                <label className={styles.formFieldLabel}>Icon</label>
                <div className={styles.iconPicker}>
                    {CUSTOM_OCCASION_ICON_OPTIONS.map(({ key, label, Icon }) => (
                        <button
                            key={key}
                            type="button"
                            className={`${styles.iconOption} ${newOccasionIcon === key ? styles.iconOptionSelected : ''}`}
                            onClick={() => setNewOccasionIcon(key)}
                            title={label}
                            aria-label={label}
                        >
                            <Icon size={16} />
                        </button>
                    ))}
                </div>

                <button
                    type="button"
                    className={`${styles.addOccasionBtn} ${styles.addOccasionBtnFull}`}
                    onClick={handleCreateCustomOccasion}
                    disabled={creatingOccasion}
                >
                    {creatingOccasion ? <RefreshCw size={13} className={styles.spinner} /> : <Plus size={13} />}
                    {' '}Add Custom Occasion
                </button>
            </div>

            {/* Commented out for now — occasion selection + custom occasion
                creation above are the only real, wired parts of this page.
                Notification Time / Reminders / Default Sender / Save have
                no backend endpoint yet (no sm_get_settings / sm_save_settings),
                so they were only ever mock. Re-enable once those exist.

            <div className={styles.sectionLabel}>Notification Time</div>
            <div className={styles.card}>
                <div className={styles.row}>
                    <span className={styles.rowLabel}>Daily digest sent at</span>
                    <input
                        type="time"
                        className={styles.timeInput}
                        value={settings.notificationTime}
                        onChange={(e) => setSettings((prev) => ({ ...prev, notificationTime: e.target.value }))}
                    />
                </div>
            </div>

            <div className={styles.sectionLabel}>Reminders</div>
            <div className={styles.card}>
                <div className={styles.row}>
                    <span className={styles.rowLabel}>Today</span>
                    <Toggle checked={settings.reminders.today} onChange={() => toggleReminder('today')} />
                </div>
                <div className={styles.row}>
                    <span className={styles.rowLabel}>One Day Before</span>
                    <Toggle checked={settings.reminders.oneDayBefore} onChange={() => toggleReminder('oneDayBefore')} />
                </div>
                <div className={styles.row}>
                    <span className={styles.rowLabel}>One Week Before</span>
                    <Toggle checked={settings.reminders.oneWeekBefore} onChange={() => toggleReminder('oneWeekBefore')} />
                </div>
            </div>

            <div className={styles.sectionLabel}>Default Sender</div>
            <div className={styles.card}>
                <div className={styles.row}>
                    <span className={styles.rowLabel}>Send wishes via</span>
                    <select
                        className={styles.senderSelect}
                        value={settings.defaultSender}
                        onChange={(e) => setSettings((prev) => ({ ...prev, defaultSender: e.target.value }))}
                    >
                        {SENDER_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                </div>
            </div>

            <button type="button" className={styles.saveBtn} onClick={handleSave}>
                <Save size={14} /> Save Settings
            </button>
            */}
        </div>
    )
}

export default CSMSettings
